'use client'

import { Fragment, useState, useMemo, useRef, useEffect } from "react";
import { CN_PRODUCT } from "@/components/product/const";
import Player from "@/components/player";
import dynamic from 'next/dynamic';

import PreviewResource from "@/components/previewResource";
import { ResourceTypeEnum, type ResourceListItem } from '@/components/previewResource/index-type'
import BottomResourceList from "@/components/previewResource/components/BottomResourceList";
import ImageZoom from "@/components/ImageZoom";
import { imageUtil } from "@/utils/imageUtil";
import NewPlaceHolderImg from "@/assets/icons/PlaceHolderImg.svg";

// styles
import { ImageShowWrapper } from "@/styles/product";
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25217";
import { dataExpTagConfig } from "@/components/product/const";
import { useParams } from "next/navigation";
type MediaProps = {
    isVideo: boolean
    targetSrc: string,
    index: number | string;
}

const CDNImage = dynamic(() => import("../cdnImage/cdnImage"), { ssr: false });
const Follow = dynamic(() => import('@/components/product/follow'), { ssr: false })

export default function ImgShowZone({ imagePartList, videoPartList, followState }: any) {
    const imgData = (videoPartList || []).concat(imagePartList || [])
    const [errorStack, setErrorStack] = useState<Record<string, string>>({})
    const [activeThumbnail, setActiveThumbnail] = useState<Record<number | string, MediaProps>>(()=> {
        const videoSrc = imgData[0]?.["C-P#video&video"]?.videoUrl
        const isVideo = !!videoSrc
        return {
            0: {
                index: 0,
                isVideo,
                targetSrc: isVideo ? videoSrc : imgData[0]?.["C-P#image&image"]?.imageUrl
            }
        }
    })
    const [mainImageLoaded, setMainImageLoaded] = useState(false); // 是否加载主图
    useEffect(() => {
      const timer = setTimeout(() => {
        setMainImageLoaded(true);
      }, 1500); // 异常兜底 避免主图无法正常渲染
      return () => clearTimeout(timer);
    }, []);
    const { sku } = useParams<{sku: string}>()
    const activeProps = useMemo(()=> {

        return { show: !errorStack[Object.keys(activeThumbnail)?.[0]], ...(Object.values(activeThumbnail)?.[0] || {})}
    }, [activeThumbnail, errorStack])

    const onImageError = (index: number | string)=> {
        setErrorStack((prevErrorStack)=> ({...prevErrorStack, [index]: index}))
    }

    const handleMouseEnter = (index: number, isVideo: boolean, targetSrc: string) => {
      setActiveThumbnail({
        [index]: { index, isVideo, targetSrc }
      })
  
      sendClickTrack.Productdetail_MainPicture_Click({
        mainskuid: sku,
        madiaType: isVideo ? "video" : "picture",
        index
      })
    }
    
    const mediaContentRef = useRef<HTMLDivElement>(null);
    const [previewState, setPreviewState] = useState(false);
    const resourceList = useMemo(() => {
      const list: ResourceListItem[] = [];
      (videoPartList || []).forEach((item: any) => {
        if (item["C-P#video&video"]?.videoUrl) {
          list.push({
            type: ResourceTypeEnum.Video,
            url: item['C-P#video&video'].videoUrl,
            coverImageUrl: item["C-P#video&video"]?.coverImageUrl || ''
          })
        }
      });
  
      (imagePartList || []).forEach((item: any) => {
        if (item["C-P#image&image"]?.imageUrl) {
          list.push({
            type: ResourceTypeEnum.Image,
            url: item["C-P#image&image"]?.imageUrl
          })
        }

        (item["C-P#image&image"]?.supplementaryImageList || []).forEach((imgUrl: string) => {
          list.push({
            type: ResourceTypeEnum.Image,
            url: imgUrl
          })
        });
      });
  
      return list
    }, [imagePartList, videoPartList])
  
    const previewResource = () => {
      setPreviewState(true);

      if (activeProps.isVideo && mediaContentRef.current) {
        const videoEle = mediaContentRef.current.querySelector('video');
        videoEle?.pause();
      }
    }

    const [defaultResourceUrl, setDefaultResourceUrl] = useState<string | undefined>(undefined);
    const changeResourceIndex = (newIndex: number) => {
      const currentInfo = resourceList.find((_, index: number) => index === newIndex);
      setDefaultResourceUrl(currentInfo?.url);
    }

  return (
    <ImageShowWrapper>
      <div className={CN_PRODUCT("thumbnails-list")}>
        {imgData.map((item: any, index: number) => {

            const videoCoverImgSrc = item["C-P#video&video"]?.coverImageUrl
            const videoSourceSrc = item["C-P#video&video"]?.videoUrl

            const isVideo = !!videoCoverImgSrc

            const imgSrc = videoCoverImgSrc || item["C-P#image&image"]?.imageUrl

            // supplementaryImageList
            const supplementaryImageList = item["C-P#image&image"]?.supplementaryImageList
            return <Fragment key={index}>
              <div className={`
                ${CN_PRODUCT("thumbnail")} 
                ${!!activeThumbnail[index] ? "active" : ""}
                `} 
                onMouseEnter={() => handleMouseEnter(index, isVideo, isVideo ? videoSourceSrc : imgSrc)}
                >
                {isVideo && <div className="player-tag-icon">Video</div>}
                <CDNImage src={imgSrc} width={112} height={112} alt="thumbnail" onError={()=> onImageError(index)}/>
            </div>
                {supplementaryImageList?.map((picUrl: string, index2: number)=> {
                    const keyIndex = index + '-' + index2
                    return <div className={`${CN_PRODUCT("thumbnail")} ${!!activeThumbnail[keyIndex] ? "active" : ""}`} key={keyIndex} onMouseEnter={() => handleMouseEnter(keyIndex, false, picUrl)}>

                        <CDNImage src={picUrl} width={112} height={112} alt="thumbnail" onError={()=> onImageError(keyIndex)}/>
                    </div>
                })}
            </Fragment>
        })}
      </div>
        <div 
            className="media-content"
            exptag={"exp|" + EXPOSURE_RECORD.Productdetail_MainPicture_Expo}
            data-exptag-config={dataExpTagConfig}
            data-exptag-json-param={
                JSON.stringify({
                  mainskuid: sku,
                  madiaType: activeProps.isVideo ? "video" : "picture",
                  index: activeProps?.index || 0
                })
              }

            ref={mediaContentRef}
            onClick={previewResource}
        >
            {
                activeProps.isVideo ? <div style={{ position: 'relative' }}>
                  {/* 主图占位图 - 确保优先加载 */}
                  <NewPlaceHolderImg
                    style={{ 
                      opacity: mainImageLoaded ? 0 : 1,
                      transition: 'opacity 0.3s',
                      position: mainImageLoaded ? 'absolute' : 'relative'
                    }}
                  />
                        
                  {/* 真实主图 在占位图渲染完成之后展示 */}
                  <Player
                    url={`${activeProps.targetSrc}`}
                    width={624}
                    height={624}
                    style={{
                      opacity: mainImageLoaded ? 1 : 0,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transition: 'opacity 0.3s'
                    }}
                    onPlayTrack={(status)=> {
                        sendClickTrack.Productdetail_BigVideo_Click({
                            mainskuid: sku,
                            playStatus: status
                        })
                    }}
                    onVideoReady={() => {
                      setMainImageLoaded(true);
                    }}
                  />
                </div> :  
                <ImageZoom 
                  imgUrl={imageUtil.concatImgUrlWithoutForceHttps(activeProps.targetSrc, 1248, 1248, 70)}
                  containerWidth={624}
                  containerHeight={624}
                  zoomedAreaWidth={312}
                  zoomedAreaHeight={411}
                  zoomedImageBoxWidth={496}
                >
                  <div className={CN_PRODUCT("big-img")} style={{ position: 'relative' }}>
                    {/* 主图占位图 - 确保优先加载 */}
                    <NewPlaceHolderImg
                      style={{ 
                        opacity: mainImageLoaded ? 0 : 1,
                        transition: 'opacity 0.3s',
                        position: mainImageLoaded ? 'absolute' : 'relative'
                      }}
                    />

                    <CDNImage
                      src={activeProps.targetSrc}
                      width={624}
                      height={624}
                      alt="thumbnail"
                      style={{
                        opacity: mainImageLoaded ? 1 : 0,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transition: 'opacity 0.3s'
                      }}
                      onLoad={() => {
                        setMainImageLoaded(true);
                      }}
                    />
                  </div>
                </ImageZoom>
            }
            <Follow followState={followState}/>
        </div>

        {
          previewState && 
            <PreviewResource 
              open={previewState} 
              defaultUrl={defaultResourceUrl ?? activeProps.targetSrc} 
              resourceList={resourceList}
              closeHandler={() => { 
                setPreviewState(false);
                setDefaultResourceUrl(undefined);
              }}
              bottomSlot={(currentIndex: number) => (
                <BottomResourceList 
                  resourceList={resourceList} 
                  currentIndex={currentIndex} 
                  changeIndexHandler={changeResourceIndex}
                />
              )}
            />
        }
    </ImageShowWrapper>
  );
}
